import { useEffect, useState } from "react";
import { useUser } from "../hooks/useQueries";
import { NavLink, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Input from "../components/shared/Input";
import { useSearchParams } from "react-router-dom";
import ActiveSessionModal from "../components/shared/ActiveSessionModal";
import { XMarkIcon } from "@heroicons/react/24/outline";

export default function Login() {
  const navigate = useNavigate();

  const { login, isLoginLoading, verifyEmail } = useUser();

  // holds {startedAt, ip, credentials} while the confirm dialog is open; credentials stay in memory only
  const [activeSession, setActiveSession] = useState(null);

  const validationSchema = yup.object().shape({
    password: yup.string().required("Required"),
    email: yup.string().email().required("Required"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { email: "", password: "" },
  });

  const registerClickHandler = () => {
    navigate("/register");
  };

  const onSubmit = (data, e) => {
    login(data, {
      onError: (error) => {
        if (error?.response?.status === 409 && error.response.data?.code === "ACTIVE_SESSION") {
          setActiveSession({
            startedAt: error.response.data.startedAt,
            ip: error.response.data.ip,
            credentials: data,
          });
        }
      },
    });
  };

  const confirmForceLogin = () => {
    if (!activeSession || isLoginLoading) return;
    login({ ...activeSession.credentials, forceLogin: true });
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const sessionTerminated = searchParams.get("reason") === "session-terminated";

  useEffect(() => {
    const token = searchParams.get("token");

    if (token) {
      verifyEmail(token);
    }
  }, []);

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* <img
              className="mx-auto h-10 w-auto"
              src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=600"
              alt="Your Company"
            />  */}
        <h2 className="mt-6 text-center text-2xl font-medium leading-9 tracking-tight text-gray-900">Log in to your account</h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
          {sessionTerminated && (
            <div role="alert" className="mb-6 flex items-start justify-between rounded-md bg-amber-50 p-4 ring-1 ring-inset ring-amber-200">
              <p className="text-sm text-amber-800">
                You were signed out because your account was signed in on another device.
              </p>
              <button
                type="button"
                className="ml-3 text-amber-500 hover:text-amber-700"
                aria-label="Dismiss notification"
                onClick={() => {
                  const nextParams = new URLSearchParams(searchParams);
                  nextParams.delete("reason");
                  setSearchParams(nextParams);
                }}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          )}
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <Input label="Email address" elementtype="input" type="email" name="email" {...register("email")} error={errors.email} />
              {errors.email && <span className="text-red-500 text-xs">{errors.email.message}</span>}
            </div>

            <div>
              <Input label="Password" elementtype="input" type="password" {...register("password")} error={errors.password} />
              {errors.password && <span className="text-red-500 text-xs">{errors.password.message}</span>}
            </div>

            <div className="flex items-center justify-between">
              {/* <div className="flex items-center">
                <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-400" />
                <label htmlFor="remember-me" className="ml-3 block text-sm leading-6 text-gray-900">
                  Remember me
                </label>
              </div> */}

              <div className="text-sm leading-6">
                <NavLink to="/forgot-password" className="font-semibold text-primary hover:text-primary-400">
                Forgot password?

                </NavLink>
                {/* <a href="#" className="font-semibold text-primary hover:text-primary-400">
                  Forgot password?
                </a> */}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoginLoading}
                aria-busy={isLoginLoading}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoginLoading && (
                  <span
                    className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                    aria-hidden="true"
                  />
                )}
                {isLoginLoading ? "Logging in…" : "Log in"}
              </button>
            </div>
          </form>

          {/* <div>
                <div className="relative mt-10">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm font-medium leading-6">
                    <span className="bg-white px-6 text-gray-900">Or continue with</span>
                  </div>
                </div>
  
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <a
                    href="#"
                    className="flex w-full items-center justify-center gap-3 rounded-md bg-[#1D9BF0] px-3 py-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D9BF0]"
                  >
                    <svg className="h-5 w-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                    <span className="text-sm font-semibold leading-6">Twitter</span>
                  </a>
  
                  <a
                    href="#"
                    className="flex w-full items-center justify-center gap-3 rounded-md bg-[#24292F] px-3 py-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#24292F]"
                  >
                    <svg className="h-5 w-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm font-semibold leading-6">GitHub</span>
                  </a>
                </div>
              </div> */}
        </div>

        <p className="mt-10 text-center text-sm text-gray-500">
          Not a member?{" "}
          <a className="font-semibold leading-6 text-primary hover:text-primary-400 cursor-pointer" onClick={registerClickHandler}>
            Register{" "}
          </a>
        </p>

        <ActiveSessionModal
          open={activeSession != null}
          startedAt={activeSession?.startedAt}
          ip={activeSession?.ip}
          isLoading={isLoginLoading}
          onConfirm={confirmForceLogin}
          onCancel={() => setActiveSession(null)}
        />
      </div>
    </div>
  );
}
